import { useState, useCallback, useMemo } from 'react';
import { processImage } from '../ui/processImage';

interface ProcessImageSettings {
  whitePoint?: number;
  blackPoint?: number;
  contrast?: number;
  gamma?: number;
}

/**
 * Custom hook for image processing with React memoization
 * Prevents unnecessary reprocessing of the same image with same settings
 */
export function useImageProcessing() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const processImageMemo = useCallback(
    async (imageSrc: string, settings: ProcessImageSettings = {}): Promise<string> => {
      setIsProcessing(true);
      setError(null);

      try {
        const result = await processImage({ imageSrc, settings });
        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to process image';
        setError(errorMessage);
        throw err;
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  return {
    processImage: processImageMemo,
    isProcessing,
    error
  };
}
